import { useMemo } from 'react';
import { useOutputData } from './OutputDataContext';
import {
  COMPOSITE_DOOR_GEARING_GROUP,
  HARDWARE_PACKING_GROUP,
  COMPOSITE_DOOR_PACKING_GROUP
} from './outputPerformanceShared';
import {
  getMatchingRecords,
  getRecordCount,
  getBoxesAndItems,
  getWeldedCillStats,
  getPersonName,
  getItemTypeBreakdown,
  groupWeldedCillRecords,
  groupBoxRecords,
  groupRecordsWithItemTypeDetail
} from './outputPerformanceAdapter';

const getEffectiveMetricType = (group) => {
  if (group.name === COMPOSITE_DOOR_GEARING_GROUP) return 'compositeDoorGearing';
  if (group.name === HARDWARE_PACKING_GROUP) return 'hardwarePacking';
  if (group.name === COMPOSITE_DOOR_PACKING_GROUP) return 'compositeDoorPacking';
  return group.metricType || 'items';
};

// --- Data source resolution ---
// metricType (from metadata or group override) determines which pool to use:
//
//   weldedCill            → pools.weldedCill (matched by station)
//   piecesCut             → pools.piecesCut, fallback pools.production
//   frameBoxes            → pools.frameBoxes, fallback pools.production
//   compositeDoorGearing  → pools.compositeDoorGearing (all records, not filtered by station)
//   hardwarePacking       → pools.hardwarePacking (matched by station, weldedCill-style counting)
//   compositeDoorPacking  → pools.hardwarePacking (matched by station, weldedCill-style counting)
//   items / sashes / *    → pools.weldedCill if matched, else pools.readyToLoad, else pools.production
const resolveRecords = (pools, metricType, stationConfig) => {
  const productionMatched = getMatchingRecords(pools.production, stationConfig);

  switch (metricType) {
    case 'weldedCillWelding': {
      const matched = getMatchingRecords(pools.weldedCillWelding, stationConfig);
      return { stationRecords: [], weldedCillRecords: matched, isWeldedCill: true };
    }
    case 'weldedCill': {
      const matched = getMatchingRecords(pools.weldedCill, stationConfig);
      return { stationRecords: [], weldedCillRecords: matched, isWeldedCill: true };
    }
    case 'hardwarePacking':
    case 'compositeDoorPacking': {
      const matched = getMatchingRecords(pools.hardwarePacking, stationConfig);
      return { stationRecords: [], weldedCillRecords: matched, isWeldedCill: true, isPacking: true };
    }
    case 'piecesCut': {
      const piecesMatched = getMatchingRecords(pools.piecesCut, stationConfig);
      return {
        stationRecords: piecesMatched.length ? piecesMatched : productionMatched,
        weldedCillRecords: [],
        isWeldedCill: false
      };
    }
    case 'frameBoxes': {
      const frameBoxMatched = getMatchingRecords(pools.frameBoxes, stationConfig);
      return {
        stationRecords: frameBoxMatched.length ? frameBoxMatched : productionMatched,
        weldedCillRecords: [],
        isWeldedCill: false
      };
    }
    case 'compositeDoorGearing':
      return {
        stationRecords: pools.compositeDoorGearing,
        weldedCillRecords: [],
        isWeldedCill: false
      };
    default: {
      const weldedCillMatched = getMatchingRecords(pools.weldedCill, stationConfig);
      if (weldedCillMatched.length > 0) {
        return { stationRecords: [], weldedCillRecords: weldedCillMatched, isWeldedCill: true };
      }
      const weldedCillWeldingMatched = getMatchingRecords(pools.weldedCillWelding, stationConfig);
      if (weldedCillWeldingMatched.length > 0) {
        return { stationRecords: [], weldedCillRecords: weldedCillWeldingMatched, isWeldedCill: true };
      }
      const readyToLoadMatched = getMatchingRecords(pools.readyToLoad, stationConfig);
      return {
        stationRecords: readyToLoadMatched.length ? readyToLoadMatched : productionMatched,
        weldedCillRecords: [],
        isWeldedCill: false
      };
    }
  }
};

const buildTotals = (resolved, metricType) => {
  const { stationRecords, weldedCillRecords, isWeldedCill } = resolved;
  const weldedCillStats = isWeldedCill
    ? getWeldedCillStats(weldedCillRecords)
    : { boxesCreated: 0, totalFacets: 0 };
  const boxSummary = getBoxesAndItems(stationRecords);

  return {
    total: isWeldedCill ? weldedCillStats.boxesCreated : getRecordCount(stationRecords, metricType),
    hasBoxData: boxSummary.hasBoxData,
    itemsCount: boxSummary.itemsCount,
    boxesCount: boxSummary.boxesCount,
    hasWeldedCillData: isWeldedCill,
    weldedCillBoxesCreated: weldedCillStats.boxesCreated,
    weldedCillTotalFacets: weldedCillStats.totalFacets
  };
};

const buildBreakdowns = (resolved, metricType, hasBoxData, frameBoxRecords) => {
  const { stationRecords, weldedCillRecords, isWeldedCill } = resolved;

  const build = (groupBy) => {
    if (isWeldedCill) {
      return groupWeldedCillRecords(weldedCillRecords, groupBy);
    }
    if (hasBoxData) {
      return groupBoxRecords(stationRecords, groupBy);
    }
    return groupRecordsWithItemTypeDetail(stationRecords, groupBy, metricType, frameBoxRecords);
  };

  return {
    shiftBreakdown: build(record => record.shift || 'N/A'),
    peopleBreakdown: build(getPersonName)
  };
};

const collectSource = records => {
  if (!records.length) return 'none';
  return records.some(r => r.source === 'api') ? 'api' : 'none';
};

export function useStationData(metricType, stationConfig) {
  const { pools } = useOutputData();

  return useMemo(() => {
    const resolved = resolveRecords(pools, metricType, stationConfig);
    const totals = buildTotals(resolved, metricType);
    const frameBoxRecords = getMatchingRecords(pools.frameBoxes, stationConfig);

    const stationHasItemType = resolved.stationRecords.some(
      r => r.itemType || r.ItemType || r.itemTypeName || r.ItemTypeName
    );
    const itemTypeSource = stationHasItemType ? resolved.stationRecords : frameBoxRecords;

    const breakdowns = buildBreakdowns(resolved, metricType, totals.hasBoxData, frameBoxRecords);

    const allRecords = resolved.isWeldedCill ? resolved.weldedCillRecords : resolved.stationRecords;

    return {
      records: allRecords,
      source: collectSource(allRecords),
      ...totals,
      weldedCillFacetsLabel: resolved.isPacking ? 'Items' : undefined,
      itemTypeBreakdown: itemTypeSource.length ? getItemTypeBreakdown(itemTypeSource) : null,
      ...breakdowns
    };
  }, [pools, metricType, stationConfig]);
}

export function useAreaTotal(areaConfig) {
  const { pools } = useOutputData();

  return useMemo(() => {
    if (!areaConfig?.groups) return 0;

    let total = 0;
    for (const group of areaConfig.groups) {
      const metricType = getEffectiveMetricType(group);
      for (const station of group.stations || []) {
        const resolved = resolveRecords(pools, metricType, station);
        const totals = buildTotals(resolved, metricType);
        total += totals.total;
      }
    }
    return total;
  }, [pools, areaConfig]);
}

export function useFactoryTotal(factoryConfig) {
  const { pools } = useOutputData();

  return useMemo(() => {
    if (!factoryConfig?.areas) return 0;

    let total = 0;
    for (const area of factoryConfig.areas) {
      for (const group of area.groups || []) {
        const metricType = getEffectiveMetricType(group);
        for (const station of group.stations || []) {
          const resolved = resolveRecords(pools, metricType, station);
          const totals = buildTotals(resolved, metricType);
          total += totals.total;
        }
      }
    }
    return total;
  }, [pools, factoryConfig]);
}
